import { SnackbarProvider as NotistackProvider } from 'notistack';
import React from 'react';
import { SnackbarContext, useSnackbarInternal } from './useSnackbar';

/**
 * Minimizing the set of props exposed for SnackbarProvider, since not all props are used in the codebase.
 * We also specifically want to avoid exposing styling props to keep the look and feel consistent.
 */
export interface MinimalSnackbarProviderProps {
  children: React.ReactNode;
  /** Position of the snackbar on the screen */
  anchorOrigin?: {
    horizontal: 'left' | 'right' | 'center';
    vertical: 'top' | 'bottom';
  };
  /** Duration in milliseconds before snackbar automatically hides */
  autoHideDuration?: number;
  /** Custom components for different snackbar variants */
  Components?: {
    default?: React.ComponentType<Record<string, unknown>>;
    error?: React.ComponentType<Record<string, unknown>>;
    info?: React.ComponentType<Record<string, unknown>>;
    success?: React.ComponentType<Record<string, unknown>>;
    warning?: React.ComponentType<Record<string, unknown>>;
  };
  /** Maximum number of snackbars that can be stacked */
  maxSnack?: number;
  /** Prevent duplicate snackbars with the same message */
  preventDuplicate?: boolean;
}

/**
 * Wrapper for the 'notistack' snackbar provider that provides some convenience
 * methods for showing snackbars.
 */
export function SnackbarProvider(props: MinimalSnackbarProviderProps) {
  const { children, ...notistackProps } = props;
  return (
    <NotistackProvider {...notistackProps}>
      <SnackbarProviderInner>{children}</SnackbarProviderInner>
    </NotistackProvider>
  );
}

// Actual provider component since we need access to the Notistack context
function SnackbarProviderInner(props: { children: React.ReactNode }) {
  const context = useSnackbarInternal();

  return <SnackbarContext.Provider value={context}>{props.children}</SnackbarContext.Provider>;
}
